import type {
  ColumnDef,
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  Row,
  RowSelectionState,
  VisibilityState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TableStateControl, UseTableSettingsReturn } from '../../hooks';
import { createEmptyFilterGroup, deriveFilterTypesFromColumns } from '../../hooks/useTableStateControl.utils';
import type { FilterGroupItem, FilterShortcut, SavedFilter } from '../../utils/filterSettings';
import { BulkActionOverlay, type BulkActionOverlayAction } from '../BulkActionOverlay';
import { ErrorBoundary, TableErrorDisplay } from '../ErrorBoundary';
import { Table, type TableColumn } from '../Table';
import { flattenColumns } from '../Table/utils';
import { TableActionRow, type ToolbarShortcut } from '../TableActionRow';
import { DEFAULT_DENSITY, type Density, type ExportScope, type TableMenuAction } from '../TableMenu';
import {
  getDefaultViewState,
  queryDiffersFromDefault,
  viewChangesFromDefault,
  viewDiffersFromDefault,
} from './viewDefaults';

/**
 * Selection state for server-side tables. Use for bulk actions.
 * - partial: explicit list of selected row ids (e.g. after selecting individual rows or after "select all" then deselecting some).
 * - all: every row matching current filters is selected, except those in excludedIds (e.g. "select all" then user unchecked a few).
 */
export type ServerSideSelectionState =
  | { mode: 'partial'; selectedIds: string[] }
  | { mode: 'all'; excludedIds: string[] };

/** Props `ServerTable` and `ClientTable` share; each adds its own data, selection, bulk action and export props. */
export type TableBaseProps<TData> = {
  id: string;
  columns: TableColumn<TData>[];
  isLoading?: boolean;
  isFetching?: boolean;
  enableRowSelection?: boolean;
  enableColumnGrouping?: boolean;
  defaultColumn?: Partial<ColumnDef<TData>>;
  /**
   * Stable row id (e.g. from API). When enableRowSelection is true, defaults to (row) => row.id so server-side selection works across pages.
   */
  getRowId?: (row: TData) => string;
  getRowLabel?: (row: TData) => string;
  /**
   * When this value changes, the table clears its internal selection state
   * (`selectedIds`, `selectAll`, and `excludedIds`). Useful after a successful
   * bulk action so consumers don't have to remount the entire table to drop
   * the now-stale selection. Bump a counter or pass a fresh reference.
   */
  selectionResetKey?: number | string;
  className?: string;
  /** Test id for the table's root, so a page can find its table (e.g. `utilities-table`). */
  'data-testid'?: string;
  /** Names the table for screen readers; prefer `aria-labelledby` when a visible heading sits above it. */
  'aria-label'?: string;
  /** Id of the visible heading that names the table for screen readers. */
  'aria-labelledby'?: string;
  /** Opt-in: also expose the export actions as a split button in the left toolbar cluster, after the filter button. */
  showExportSplitButton?: boolean;
  /** Export in progress ('all' or 'visible'); the matching export control shows a spinner. */
  exporting?: ExportScope;
  /** Extra settings-menu actions, such as "Download all files", shown after the export buttons. */
  menuActions?: TableMenuAction[];
  onMyFilters?: () => void;
  onMyFiltersMouseEnter?: () => void;
  myFiltersLabel?: React.ReactNode;
  isMyFiltersActive?: boolean;
  filterShortcuts?: FilterShortcut[];
  /** User-saved filter sets, shown in the filter dialog. Each applies like a shortcut. */
  savedFilters?: SavedFilter[];
  /** Save the current filter set under a name. When omitted, the save control is hidden. */
  onSaveFilter?: (name: string, filter: FilterGroupItem) => void;
  /** Remove a saved filter by id. */
  onRemoveSavedFilter?: (id: string) => void;
  toolbarShortcuts?: ToolbarShortcut[];
  /** Custom controls (e.g. a dropdown) rendered at the end of the left toolbar cluster. */
  toolbarContent?: React.ReactNode;
  onBookmarks?: () => void;
  onBookmarksMouseEnter?: () => void;
  isBookmarksActive?: boolean;
  /** Custom filter pills (e.g. "Map filtering Table") shown alongside regular filters. */
  customFilterPills?: Array<{ id: string; label: string; onRemove: () => void; labelOnly?: boolean }>;
  /**
   * When true (default), row selection allows multiple rows. When false, only one row can be selected at a time.
   */
  enableMultiRowSelection?: boolean;
  /** When true, clicking anywhere on a row toggles its selection. Defaults to false (checkbox only). */
  enableRowClickSelection?: boolean;
  /**
   * Called with the row when it's clicked, after any click-to-select toggle. A mouse shortcut only: what it does must
   * also be reachable from a focusable control. Every click in the row counts, like click-to-select; a control that
   * shouldn't trigger it calls `event.stopPropagation()`.
   */
  onRowClick?: (row: TData) => void;
  /** Optional empty state shown when table has no data (rowCount 0), not loading, and not fetching. */
  emptyState?: React.ReactNode;
  /** Accordion rows: when true, rows returned by getSubRows render as expandable children. */
  enableExpanding?: boolean;
  /** Returns a row's child rows. Required for enableExpanding to have any effect. */
  getSubRows?: (row: TData) => TData[] | undefined;
  /** Set false to make accordion child rows unselectable: no cascade from the parent, no checkbox. */
  enableSubRowSelection?: boolean;
  /** Column that grows into spare width, so the table fills the page without scrolling sideways. */
  expandColumnId?: string;
  /** Show the search box. Defaults to true. */
  enableSearch?: boolean;
  /** Show the filter button and applied-filter pills. Defaults to true. */
  enableFilters?: boolean;
  /** Show the settings menu: density, page size, export and reset view. Defaults to true. */
  enableSettings?: boolean;
  /** Let users hide columns from the columns dialog, the header menu or by dragging a header away. Defaults to true. */
  enableHiding?: boolean;
  /** Let users pin columns from the header menu, and by dragging between pinned groups. Defaults to true. */
  enableColumnPinning?: boolean;
  /** Let users reorder columns by dragging headers or rows in the columns dialog. Defaults to true. */
  enableColumnOrdering?: boolean;
  /** Row density the table starts at and Reset View restores, until the user picks one. Defaults to cozy. */
  defaultDensity?: Density;
};

/** What "Export Visible" and "Export All" receive, so the export can follow the table's column layout. */
export type TableExportParams<TData> = {
  data: TData[];
  columns: TableColumn<TData>[];
  columnOrder: string[];
  columnVisibility: VisibilityState;
};

export type TableShellProps<TData> = TableBaseProps<TData> & {
  /** The rows to show: one page, already filtered, searched and sorted. */
  data: TData[];
  /** Rows matching the current filters and search, across every page. */
  rowCount: number;
  stateControl: TableStateControl;
  /** Saved view settings for `id`; the table reads them once on mount and saves changes back. */
  settings: UseTableSettingsReturn;
  enablePagination: boolean;
  onSelectionChange?: (selection: ServerSideSelectionState) => void;
  initialSelectionState?: ServerSideSelectionState;
  bulkActions?: BulkActionOverlayAction[];
  onExportVisible?: (params: TableExportParams<TData>) => void;
  onExportAll?: (params: TableExportParams<TData>) => void;
};

/**
 * The toolbar, settings, selection, bulk actions and grid that `ServerTable` and `ClientTable` share. It renders the
 * page of rows it's given; each table decides where those rows come from. Internal: not exported from the package.
 */
function TableShell<TData>({
  id,
  columns,
  data,
  rowCount,
  settings,
  stateControl,
  isLoading,
  isFetching,
  className,
  'data-testid': dataTestId,
  enableRowSelection = true,
  enableColumnGrouping = true,
  getRowId,
  getRowLabel,
  onSelectionChange,
  initialSelectionState,
  selectionResetKey,
  defaultColumn,
  bulkActions,
  enableMultiRowSelection = true,
  enableRowClickSelection = false,
  onRowClick,
  emptyState,
  // actions
  onExportVisible,
  onExportAll,
  showExportSplitButton,
  exporting,
  menuActions,
  onMyFilters,
  onMyFiltersMouseEnter,
  myFiltersLabel,
  isMyFiltersActive,
  onBookmarks,
  onBookmarksMouseEnter,
  isBookmarksActive,
  filterShortcuts,
  savedFilters,
  onSaveFilter,
  onRemoveSavedFilter,
  toolbarShortcuts,
  toolbarContent,
  customFilterPills,
  enableSearch,
  enableFilters = true,
  enableSettings,
  enableHiding = true,
  enableColumnPinning = true,
  enableColumnOrdering = true,
  enablePagination,
  defaultDensity = DEFAULT_DENSITY,
  ...tableOptions
}: TableShellProps<TData>) {
  const {
    initialSpacing,
    initialColumnOrder,
    initialColumnPinning,
    initialColumnVisibility,
    initialColumnSizing,
    saveSettings,
  } = settings;

  // When enableRowSelection is true, use server-side selection by default (stable ids across pages, select all across pages).
  // Default getRowId uses row.id or row.uuid; override via prop if your rows use a different id field.
  const effectiveGetRowId = useMemo(() => {
    if (!enableRowSelection) return getRowId;
    if (getRowId) return getRowId;
    return (row: TData, index?: number) => {
      const r = row as Record<string, unknown>;
      if (r.id != null) return String(r.id);
      if (r.uuid != null) return String(r.uuid);
      return `row-${index ?? 0}`;
    };
  }, [enableRowSelection, getRowId]);

  // Flatten columns to get all leaf columns
  const leafColumns: TableColumn<TData>[] = useMemo(() => flattenColumns(columns as any[]), [columns]);

  /** Columns with meta.defaultVisible === false are hidden when no saved visibility exists. */
  const defaultColumnVisibility = useMemo(() => {
    const state: VisibilityState = {};
    leafColumns.forEach((col) => {
      const meta = (col as { meta?: { defaultVisible?: boolean } }).meta;
      if (meta?.defaultVisible === false && col.id) {
        state[col.id] = false;
      }
    });
    return Object.keys(state).length ? state : undefined;
  }, [leafColumns]);

  // View state the "Reset View" action restores for this table instance
  const defaultViewState = useMemo(
    () =>
      getDefaultViewState({
        leafColumnIds: leafColumns.map((col) => col.id),
        enableRowSelection,
        defaultColumnVisibility,
        defaultPageSize: stateControl.defaultPageSize,
        defaultDensity,
      }),
    [leafColumns, enableRowSelection, defaultColumnVisibility, stateControl.defaultPageSize, defaultDensity],
  );

  // Derive filterTypes and operatorTypes from columns - these are used by AdvancedFilterDialog
  const filterTypes = useMemo(() => deriveFilterTypesFromColumns(leafColumns), [leafColumns]);

  // Internal state: use Sets for storage; convert to array only when passing to parent or to Table.
  const [selectedIdsSet, setSelectedIdsSet] = useState<Set<string>>(() =>
    initialSelectionState?.mode === 'partial' ? new Set(initialSelectionState.selectedIds) : new Set(),
  );
  const [selectAll, setSelectAll] = useState(() => initialSelectionState?.mode === 'all');
  const [excludedIdsSet, setExcludedIdsSet] = useState<Set<string>>(() =>
    initialSelectionState?.mode === 'all' ? new Set(initialSelectionState.excludedIds ?? []) : new Set(),
  );
  // Saved hidden and pinned columns apply only while their controls are on, so users can always undo them.
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => (enableHiding ? initialColumnVisibility : undefined) ?? defaultColumnVisibility ?? {},
  );
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(initialColumnSizing ?? {});
  const [density, setDensity] = useState<Density>((initialSpacing as Density) ?? defaultDensity);

  // Initialize column order with all column IDs, or from saved settings
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
    if (initialColumnOrder) {
      return initialColumnOrder;
    }
    const columnIds = leafColumns.map((col) => col.id);
    return enableRowSelection ? ['select', ...columnIds] : columnIds;
  });

  // Initialize column pinning from saved settings
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(() => {
    if (enableColumnPinning && initialColumnPinning) {
      return initialColumnPinning;
    }
    return {
      left: enableRowSelection ? ['select'] : [],
      right: [],
    };
  });

  // Server-side selection: only when enableRowSelection — notify parent when selection changes (convert Sets to arrays only here)
  const selectionState: ServerSideSelectionState = useMemo(
    () =>
      enableRowSelection
        ? selectAll
          ? { mode: 'all', excludedIds: [...excludedIdsSet] }
          : { mode: 'partial', selectedIds: [...selectedIdsSet] }
        : { mode: 'partial', selectedIds: [] },
    [enableRowSelection, selectAll, excludedIdsSet, selectedIdsSet],
  );
  useEffect(() => {
    if (enableRowSelection) onSelectionChange?.(selectionState);
  }, [enableRowSelection, onSelectionChange, selectionState]);

  // Imperative selection reset hook: when consumers bump selectionResetKey,
  // wipe the three internal selection sets. Skip the initial render so an
  // undefined-by-default selectionResetKey doesn't blow away initialSelectionState.
  const isFirstSelectionResetRender = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectionResetKey is a trigger-only dependency — the effect intentionally fires on key change without reading it
  useEffect(() => {
    if (isFirstSelectionResetRender.current) {
      isFirstSelectionResetRender.current = false;
      return;
    }
    setSelectedIdsSet(new Set());
    setExcludedIdsSet(new Set());
    setSelectAll(false);
  }, [selectionResetKey]);

  // Row selection Record for the Table (derived from Set when not selectAll)
  const rowSelectionForTable: RowSelectionState = useMemo(
    () => Object.fromEntries([...selectedIdsSet].map((id) => [id, true])),
    [selectedIdsSet],
  );

  // Server-side selection when enableRowSelection is true (uses effectiveGetRowId by default).
  // Select-all is scoped to the filtered result set (rowCount = total rows matching current filters).
  // When not in selectAll mode, header shows checked when selected count equals total table rows (user manually selected all).
  const serverSideSelection = useMemo(() => {
    if (!effectiveGetRowId || !enableRowSelection) return undefined;
    const getIsRowSelected = (rowId: string) => (selectAll ? !excludedIdsSet.has(rowId) : selectedIdsSet.has(rowId));
    const isSomeSelected = selectAll ? excludedIdsSet.size < rowCount : selectedIdsSet.size > 0;
    const isAllSelected = selectAll
      ? excludedIdsSet.size === 0 && rowCount > 0
      : rowCount > 0 && selectedIdsSet.size === rowCount;
    return {
      isAllSelected,
      isSomeSelected,
      getIsRowSelected,
      onSelectAllChange: (selected: boolean) => {
        if (selected) {
          setExcludedIdsSet(new Set());
          setSelectedIdsSet(new Set());
          setSelectAll(true);
        } else {
          setSelectAll(false);
          setExcludedIdsSet(new Set());
          setSelectedIdsSet(new Set());
        }
      },
      onRowToggle: (rowId: string, selected: boolean) => {
        if (!enableMultiRowSelection) {
          // Single selection: replace selection with just this row, or clear if deselecting
          setSelectAll(false);
          setExcludedIdsSet(new Set());
          setSelectedIdsSet(selected ? new Set([rowId]) : new Set());
        } else if (selectAll) {
          setExcludedIdsSet((prev) => {
            const next = new Set(prev);
            if (selected) next.delete(rowId);
            else next.add(rowId);
            return next;
          });
        } else {
          setSelectedIdsSet((prev) => {
            const next = new Set(prev);
            if (selected) next.add(rowId);
            else next.delete(rowId);
            return next;
          });
        }
      },
    };
  }, [
    effectiveGetRowId,
    enableRowSelection,
    enableMultiRowSelection,
    selectAll,
    excludedIdsSet,
    selectedIdsSet,
    rowCount,
  ]);

  // When using server-side selection, pass derived rowSelection; when selectAll we pass {} so table doesn't hold stale ids
  const effectiveRowSelection = effectiveGetRowId && selectAll ? {} : rowSelectionForTable;
  const handleRowSelectionChange = useCallback(
    (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      const next = typeof updater === 'function' ? updater(rowSelectionForTable) : updater;
      const ids = Object.keys(next).filter((k) => next[k]);
      setSelectedIdsSet(new Set(ids));
      if (effectiveGetRowId) setSelectAll(false);
    },
    [rowSelectionForTable, effectiveGetRowId],
  );

  // Persist only what the user changed, so a dimension left alone keeps following the table's defaults
  useEffect(() => {
    const changes = viewChangesFromDefault(
      {
        columnOrder,
        columnVisibility,
        columnPinning,
        columnSizing,
        pageSize: stateControl.pagination.pageSize,
        density,
      },
      defaultViewState,
    );
    saveSettings({
      spacing: changes.density,
      pageSize: changes.pageSize,
      columnOrder: changes.columnOrder,
      columnPinning: changes.columnPinning,
      columnVisibility: changes.columnVisibility,
      columnSizing: changes.columnSizing,
    });
  }, [
    density,
    stateControl.pagination.pageSize,
    columnOrder,
    columnPinning,
    columnVisibility,
    columnSizing,
    defaultViewState,
    saveSettings,
  ]);

  const handleRowClick = useCallback((row: Row<TData>) => onRowClick?.(row.original), [onRowClick]);

  const exportParams = useMemo(
    () => ({ data, columns: leafColumns, columnOrder, columnVisibility }),
    [data, leafColumns, columnOrder, columnVisibility],
  );

  // Restores column layout, page size and density to the instance defaults
  const handleResetView = useCallback(() => {
    setColumnOrder(defaultViewState.columnOrder);
    setColumnVisibility(defaultViewState.columnVisibility);
    setColumnPinning(defaultViewState.columnPinning);
    setColumnSizing(defaultViewState.columnSizing);
    setDensity(defaultViewState.density);
    if (stateControl.resetToDefaults) {
      stateControl.resetToDefaults();
      return;
    }
    // Fallback for state controls without resetToDefaults; setPageSize also returns to page 1.
    stateControl.setFilters(stateControl.defaultFilters ?? createEmptyFilterGroup());
    stateControl.setGlobalFilter('');
    stateControl.setSorting(stateControl.defaultSorting ?? []);
    stateControl.setPageSize(defaultViewState.pageSize);
  }, [defaultViewState, stateControl]);

  // Rows the next bulk action would touch; select-all counts every row minus the unchecked ones.
  const selectedCount = useMemo(
    () => (enableRowSelection ? (selectAll ? Math.max(rowCount - excludedIdsSet.size, 0) : selectedIdsSet.size) : 0),
    [enableRowSelection, selectAll, rowCount, excludedIdsSet, selectedIdsSet],
  );

  const hasSelection =
    enableRowSelection && (selectionState.mode === 'partial' ? selectionState.selectedIds.length > 0 : true);

  const handleClearSelection = useCallback(() => {
    setSelectAll(false);
    setExcludedIdsSet(new Set());
    setSelectedIdsSet(new Set());
    onSelectionChange?.({ mode: 'partial', selectedIds: [] });
  }, [onSelectionChange]);

  const canResetView = useMemo(
    () =>
      viewDiffersFromDefault(
        {
          columnOrder,
          columnVisibility,
          columnPinning,
          columnSizing,
          pageSize: stateControl.pagination.pageSize,
          density,
        },
        defaultViewState,
      ) ||
      queryDiffersFromDefault(
        { filters: stateControl.filters, globalFilter: stateControl.globalFilter, sorting: stateControl.sorting },
        { filters: stateControl.defaultFilters, sorting: stateControl.defaultSorting },
      ),
    [
      columnOrder,
      columnVisibility,
      columnPinning,
      columnSizing,
      stateControl.pagination.pageSize,
      stateControl.filters,
      stateControl.globalFilter,
      stateControl.sorting,
      stateControl.defaultSorting,
      stateControl.defaultFilters,
      density,
      defaultViewState,
    ],
  );

  return (
    <ErrorBoundary
      fallback={(error) => <TableErrorDisplay error={error} />}
      onError={(error, errorInfo) => {
        console.error('Table error:', error, errorInfo);
      }}
    >
      <div className={`flex flex-col gap-3 ${className ?? ''}`} data-testid={dataTestId}>
        <TableActionRow
          columns={leafColumns}
          filterTypes={filterTypes}
          stateControl={stateControl}
          totalItems={rowCount}
          selectedCount={selectedCount}
          isLoading={isLoading || !!isFetching}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          columnOrder={columnOrder}
          onColumnOrderChange={setColumnOrder}
          columnPinning={columnPinning}
          onColumnPinningChange={setColumnPinning}
          density={density}
          onDensityChange={setDensity}
          onMyFilters={onMyFilters}
          onMyFiltersMouseEnter={onMyFiltersMouseEnter}
          myFiltersLabel={myFiltersLabel}
          isMyFiltersActive={isMyFiltersActive}
          onBookmarks={onBookmarks}
          onBookmarksMouseEnter={onBookmarksMouseEnter}
          isBookmarksActive={isBookmarksActive}
          customFilterPills={customFilterPills}
          onExportVisible={onExportVisible ? () => onExportVisible(exportParams) : undefined}
          onExportAll={onExportAll ? () => onExportAll(exportParams) : undefined}
          showExportSplitButton={showExportSplitButton}
          exporting={exporting}
          menuActions={menuActions}
          onResetView={handleResetView}
          canResetView={canResetView}
          filterShortcuts={filterShortcuts}
          savedFilters={savedFilters}
          onSaveFilter={onSaveFilter}
          onRemoveSavedFilter={onRemoveSavedFilter}
          toolbarShortcuts={toolbarShortcuts}
          toolbarContent={toolbarContent}
          enableSearch={enableSearch}
          enableFilters={enableFilters}
          enableSettings={enableSettings}
          enableHiding={enableHiding}
          enableColumnPinning={enableColumnPinning}
          enableColumnOrdering={enableColumnOrdering}
          enablePagination={enablePagination}
        />
        <div className="relative flex min-h-0 flex-1 flex-col">
          {emptyState && !isLoading && !isFetching && data.length === 0 && rowCount === 0 ? (
            <div className="border-grey-20 bg-grey-5 flex flex-1 flex-col items-center justify-center gap-4 self-stretch rounded border p-8">
              {emptyState}
            </div>
          ) : (
            <>
              <Table
                data={data}
                columns={columns} // Table runs on full column object, not just leaf columns
                defaultColumn={defaultColumn}
                spacing={density}
                rowSelection={effectiveRowSelection}
                onRowSelectionChange={handleRowSelectionChange}
                getRowId={effectiveGetRowId}
                getRowLabel={getRowLabel}
                serverSideSelection={serverSideSelection}
                columnOrder={columnOrder}
                onColumnOrderChange={setColumnOrder}
                columnPinning={columnPinning}
                onColumnPinningChange={setColumnPinning}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={setColumnVisibility}
                columnSizing={columnSizing}
                onColumnSizingChange={setColumnSizing}
                enableRowSelection={enableRowSelection}
                enableColumnGrouping={enableColumnGrouping}
                enableColumnResizing={!isLoading && !isFetching}
                manualSorting={true}
                manualFiltering={true}
                manualPagination={true}
                rowCount={rowCount}
                stateControl={stateControl}
                isLoading={isLoading}
                isFetching={isFetching}
                enableMultiRowSelection={enableMultiRowSelection}
                enableRowClickSelection={enableRowClickSelection}
                onRowClick={onRowClick ? handleRowClick : undefined}
                enableColumnFilters={enableFilters}
                enableHiding={enableHiding}
                enableColumnPinning={enableColumnPinning}
                enableColumnOrdering={enableColumnOrdering}
                {...tableOptions}
              />
              {enableRowSelection && bulkActions && bulkActions.length > 0 && hasSelection && (
                <BulkActionOverlay actions={bulkActions} selection={selectionState} onClose={handleClearSelection} />
              )}
            </>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export { TableShell };
